import React from "react";
import {
  ComboBox,
  Popover,
  ComboBoxProps,
  ListBox,
  ListBoxItem,
  ListBoxItemProps,
  Label,
  Input,
} from "react-aria-components";
import type { InputProps } from "react-aria-components";

import "./ReactAriaPopover.scss";
import classNames from "classnames";
import { Icon, SemanticICONS } from "semantic-ui-react";

export interface AutocompleteTextBoxProps<T extends object> extends Omit<ComboBoxProps<T>, "children"> {
  type?: "textbox" | "combobox" | "searchbox";
  label?: string;
  placeholder?: string;
  inputProps?: InputProps;
  inputRef?: React.Ref<HTMLInputElement>;
  children?: React.ReactNode | ((item: T) => React.ReactNode);
  icon?: SemanticICONS;
  id?: string;
}

interface AutocompleteTextBoxItemProps extends ListBoxItemProps {
  size?: "small" | "large";
}

/**
 * Search field with an auto-complete menu that appears when the user types
 */
export function AutocompleteTextBox<T extends object>({
  type = "textbox",
  label,
  placeholder,
  inputProps,
  inputRef,
  menuTrigger,
  allowsCustomValue = true,
  children,
  icon,
  id,
  ...props
}: AutocompleteTextBoxProps<T>) {
  const inputType = type === "searchbox" ? "search" : undefined;
  const inputRole = type;

  return (
    <ComboBox {...props} menuTrigger={menuTrigger || "focus"} allowsCustomValue={allowsCustomValue}>
      {label && <Label>{label}</Label>}

      <div className='tw-relative'>
        <Icon className='tw-absolute tw-left-4 tw-top-7 tw-text-grey' name={icon} />
        <Input
          {...inputProps}
          className={classNames(
            "tw-border-1 tw-border-solid tw-border-border-dark focus:tw-ring-2 focus:tw-ring-blue",
            {
              "tw-pl-14": icon,
              [inputProps?.className as string]: inputProps?.className,
            },
          )}
          ref={inputRef}
          type={inputType}
          placeholder={placeholder}
          role={inputRole}
          id={id}
        />
      </div>
      <Popover>
        <ListBox
          className='
            tw-w-full
            tw-overflow-hidden
            tw-rounded-md
            tw-bg-white
            tw-shadow-xl
          '
        >
          {children}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}

/**
 * Result item to display in the auto-complete menu for an
 * {@see AutocompleteTextBox}
 */
export function AutocompleteTextBoxItem({ size = "small", ...props }: AutocompleteTextBoxItemProps) {
  return (
    <ListBoxItem
      {...props}
      className={`
        tw-w-full
        tw-border-0
        tw-border-t
        tw-border-solid
        tw-border-[#2224261a]
        ${size === "large" ? "tw-p-6 tw-text-xl" : "tw-px-4 tw-py-2 tw-text-base"}
        first:tw-border-0
        data-[focused=true]:tw-bg-blue
        data-[focused=true]:tw-text-white
      `}
    />
  );
}
